// frontend/src/components/pages/admin/organizations/UpgradeRequestsTab.jsx
import React, { useEffect, useState, useMemo } from "react";
import { getAllPlanUpgradeRequests, approvePlanUpgradeRequest, rejectPlanUpgradeRequest } from "../../../../api/apiFunction/userServices";
import { toast } from "react-toastify";
import { Check, X as Cross } from "lucide-react";

const TableSkeleton = ({ columns = 7, rows = 6 }) => (
  <div>
    {/* Filter bar skeleton - matching exact UI layout */}
    <div className="flex w-full items-end gap-3 mb-4 animate-pulse">
      {/* Search input skeleton */}
      <div className="flex-1 min-w-[200px]">
        <div className="h-4 w-16 bg-bg-40 rounded mb-1" />
        <div className="h-9 w-full bg-bg-40 rounded" />
      </div>
      {/* Current Plan dropdown skeleton */}
      <div>
        <div className="h-4 w-24 bg-bg-40 rounded mb-1" />
        <div className="h-9 w-32 bg-bg-40 rounded" />
      </div>
      {/* Requested Plan dropdown skeleton */}
      <div>
        <div className="h-4 w-28 bg-bg-40 rounded mb-1" />
        <div className="h-9 w-32 bg-bg-40 rounded" />
      </div>
      {/* Status dropdown skeleton */}
      <div>
        <div className="h-4 w-14 bg-bg-40 rounded mb-1" />
        <div className="h-9 w-32 bg-bg-40 rounded" />
      </div>
    </div>

    {/* Table skeleton */}
    <div className="w-full flex justify-center">
      <div className="max-w-7xl w-full">
        <div className="bg-bg-60 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
          <table className="min-w-full divide-y divide-bd-50">
            <thead className="bg-bg-40">
              <tr>
                {[...Array(columns)].map((_, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    <div className="h-4 w-20 bg-bg-40 rounded animate-pulse" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-bg-60 divide-y divide-bd-50">
              {[...Array(rows)].map((_, i) => (
                <tr key={i}>
                  {[...Array(columns)].map((_, j) => (
                    <td key={j} className="px-4 py-4">
                      <div className="h-6 w-full bg-bg-40 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

const UpgradeRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true); // Start with true to show loader on mount
  const [actionLoading, setActionLoading] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalRequestId, setModalRequestId] = useState(null);
  const [adminResponse, setAdminResponse] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [reqSearch, setReqSearch] = useState("");
  const [reqCurrentPlan, setReqCurrentPlan] = useState("");
  const [reqRequestedPlan, setReqRequestedPlan] = useState("");
  const [reqStatus, setReqStatus] = useState("");

  // Fetch requests
  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const res = await getAllPlanUpgradeRequests();
      // Only organization requests, case-insensitive, from res.data
      setRequests(Array.isArray(res?.data) ? res.data.filter(r => (r.requesterType || '').toLowerCase() === "organization") : []);
    } catch {
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Filter options
  const reqCurrentPlanOptions = useMemo(() => Array.from(new Set(requests.map(r => r.currentPlan?.name).filter(Boolean))), [requests]);
  const reqRequestedPlanOptions = useMemo(() => Array.from(new Set(requests.map(r => r.requestedPlan?.name).filter(Boolean))), [requests]);
  const reqStatusOptions = ["pending", "approved", "rejected"];

  // Filtered requests
  const filteredRequests = useMemo(() => Array.isArray(requests) ? requests.filter(req => {
    const matchesSearch = (req.user?.name || "").toLowerCase().includes(reqSearch.toLowerCase());
    const matchesCurrentPlan = !reqCurrentPlan || req.currentPlan?.name === reqCurrentPlan;
    const matchesRequestedPlan = !reqRequestedPlan || req.requestedPlan?.name === reqRequestedPlan;
    const matchesStatus = !reqStatus || req.status === reqStatus;
    return matchesSearch && matchesCurrentPlan && matchesRequestedPlan && matchesStatus;
  }) : [], [requests, reqSearch, reqCurrentPlan, reqRequestedPlan, reqStatus]);

  // Modal logic
  const openModal = (type, requestId) => {
    setModalType(type);
    setModalRequestId(requestId);
    setAdminResponse(type === 'approve' ? "Plan upgrade approved. Welcome to your new plan!" : "Plan upgrade rejected. Please provide more details about your usage requirements.");
    setModalOpen(true);
    setConfirming(false);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalType(null);
    setModalRequestId(null);
    setAdminResponse("");
    setConfirming(false);
  };
  const handleModalSubmit = async () => {
    if (!adminResponse.trim()) {
      toast.error("Please enter a response message.");
      return;
    }
    setActionLoading(modalRequestId);
    try {
      if (modalType === 'approve') {
        await approvePlanUpgradeRequest(modalRequestId, adminResponse);
        toast.success("Request approved");
      } else {
        await rejectPlanUpgradeRequest(modalRequestId, adminResponse);
        toast.success("Request rejected");
      }
      fetchRequests();
      closeModal();
    } catch {
      toast.error(`Failed to ${modalType} request`);
    } finally {
      setActionLoading("");
    }
  };

  return (
    <div className="h-full">
      <div className="max-w-7xl mx-auto">
        {requestsLoading ? (
          <TableSkeleton columns={7} rows={6} />
        ) : (
          <>
            {/* Search & Filters for Requests */}
            <div className="flex w-full items-end gap-3 mb-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-fg-60 mb-1">Search</label>
                <input
                  type="text"
                  className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  placeholder="Search by organization name..."
                  value={reqSearch}
                  onChange={e => setReqSearch(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Current Plan</label>
                <select
                  className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  value={reqCurrentPlan}
                  onChange={e => setReqCurrentPlan(e.target.value)}
                >
                  <option value="">All</option>
                  {reqCurrentPlanOptions.map(plan => <option key={plan} value={plan}>{plan.charAt(0).toUpperCase() + plan.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Requested Plan</label>
                <select
                  className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  value={reqRequestedPlan}
                  onChange={e => setReqRequestedPlan(e.target.value)}
                >
                  <option value="">All</option>
                  {reqRequestedPlanOptions.map(plan => <option key={plan} value={plan}>{plan.charAt(0).toUpperCase() + plan.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-fg-60 mb-1">Status</label>
                <select
                  className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                  value={reqStatus}
                  onChange={e => setReqStatus(e.target.value)}
                >
                  <option value="">All</option>
                  {reqStatusOptions.map(status => <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>)}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="w-full flex justify-center">
              <div className="max-w-7xl w-full">
                <div className="bg-bg-60 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
                  <table className="min-w-full divide-y divide-bd-50">
                  <thead className="bg-bg-40">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Organization</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Current Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Requested Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Request Message</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Admin Response</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-bg-60 divide-y divide-bd-50">
                    {filteredRequests.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-fg-60 py-12">No organization plan upgrade requests found.</td>
                      </tr>
                    ) : (
                      filteredRequests.map((req) => (
                        <tr key={req._id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-80 font-semibold">{req.user?.name}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-60">{
                            (() => {
                              const planName = (req.currentPlan?.name || "Free").toLowerCase();
                              let badgeClass = "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                              if (planName === "free") badgeClass = "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                              else if (planName === "pro") badgeClass = "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                              else if (planName === "enterprise") badgeClass = "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                              return (
                                <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                  {req.currentPlan?.name || "Free"}
                                </span>
                              );
                            })()
                          }</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-60">{
                            (() => {
                              const planName = (req.requestedPlan?.name || "Free").toLowerCase();
                              let badgeClass = "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                              if (planName === "free") badgeClass = "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                              else if (planName === "pro") badgeClass = "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                              else if (planName === "enterprise") badgeClass = "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                              return (
                                <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                  {req.requestedPlan?.name || "Free"}
                                </span>
                              );
                            })()
                          }</td>
                          <td className="px-4 py-3 whitespace-pre-line break-words text-sm text-fg-60 max-w-xs min-w-[120px]">{req.requestMessage}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              const status = req.status;
                              let badgeClass = "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-200";
                              if (status === "approved") badgeClass = "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200";
                              else if (status === "rejected") badgeClass = "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200";
                              return (
                                <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                  {status?.charAt(0).toUpperCase() + status?.slice(1)}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-pre-line break-words text-sm text-fg-60 max-w-xs min-w-[120px]">{req.adminResponse}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {req.status === 'pending' ? (
                              <div className="flex gap-2">
                                <button
                                  className="p-2 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200 hover:ring-2 hover:ring-green-400/40 transition-all"
                                  title="Approve"
                                  onClick={() => openModal('approve', req._id)}
                                  disabled={actionLoading === req._id}
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  className="p-2 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200 hover:ring-2 hover:ring-red-400/40 transition-all"
                                  title="Reject"
                                  onClick={() => openModal('reject', req._id)}
                                  disabled={actionLoading === req._id}
                                >
                                  <Cross size={18} />
                                </button>
                              </div>
                            ) : (
                              <span className="inline-block px-2 py-1 rounded bg-bg-40 text-fg-60 text-xs font-medium">Performed</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          </>
        )}
      </div>
      {/* Admin Response Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-bg-60 rounded-xl shadow-lg p-6 w-full max-w-md border border-bd-50">
            <h3 className="text-lg font-bold mb-4">{modalType === 'approve' ? 'Approve Plan Upgrade' : 'Reject Plan Upgrade'}</h3>
            <label className="block text-sm font-medium text-fg-60 mb-1">Admin Response</label>
            <textarea
              className="w-full min-h-[80px] border border-bd-50 bg-bg-50 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ac-02 mb-2"
              value={adminResponse}
              onChange={e => setAdminResponse(e.target.value)}
              disabled={actionLoading === modalRequestId || confirming}
            />
            {!confirming ? (
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-2 rounded bg-bg-50 text-fg-60 font-semibold hover:bg-bg-40"
                  onClick={closeModal}
                  disabled={actionLoading === modalRequestId}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 rounded ${modalType === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white'} font-semibold`}
                  onClick={handleModalSubmit}
                  disabled={actionLoading === modalRequestId}
                >
                  {modalType === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeRequestsTab; 